import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * Canvas signature pad. Works with mouse, pen, and touch.
 * Exposes .getDataURL() and .clear() via ref.
 *   <SignaturePad ref={ref} onChange={(hasInk) => setHasSig(hasInk)} />
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const hasInkRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  useImperativeHandle(ref, () => ({
    getDataURL: () => {
      if (!canvasRef.current || !hasInkRef.current) return "";
      return canvasRef.current.toDataURL("image/png");
    },
    clear: () => clearCanvas(),
    isEmpty: () => !hasInkRef.current,
  }));

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    hasInkRef.current = false;
    setEmpty(true);
    if (onChange) onChange(false);
  };

  // Initialize canvas with proper DPI and white background
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const rect = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.floor(rect.width * dpr));
      c.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = c.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = "#0A1128";
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPoint = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = getPoint(e);
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    // Draw a small dot so a single tap still shows
    ctx.beginPath();
    ctx.arc(lastPoint.current.x, lastPoint.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "#0A1128";
    ctx.fill();
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setEmpty(false);
      if (onChange) onChange(true);
    }
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPoint(e);
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
  };
  const end = () => {
    drawing.current = false;
    lastPoint.current = null;
  };

  return (
    <div className="w-full">
      <div
        className={`relative w-full rounded-xl border-2 ${
          empty ? "border-[#E5E7EB] bg-white" : "border-[#0066FF] bg-white"
        } overflow-hidden`}
      >
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="block w-full h-40 sm:h-48 touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-[#9CA3AF] text-sm italic">Sign here with your finger or mouse</span>
          </div>
        )}
        <div className="pointer-events-none absolute bottom-2 left-3 right-3 border-t border-dashed border-[#D1D5DB]" />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-[#6B7280]">By signing you agree this is your legal electronic signature.</span>
        <button
          type="button"
          onClick={clearCanvas}
          data-testid="signature-clear"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#0A1128]"
        >
          <Eraser size={13} /> Clear
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
